import { useEffect, useState } from 'react';
import { setFatalSinkBridge } from './fatalBridge';
import { LatencyHarness } from './LatencyHarness';
import { AudioHarness } from './AudioHarness';
import { UpdateBanner } from './UpdateBanner';

/**
 * The shell — one installed app, two build-unit harnesses.
 *
 * **There is deliberately only one deployable.** Andrew installs this once to the home screen and
 * runs U-0's latency measurement and U-1's `MediaRecorder` spike in the same device session, at the
 * same venue, on the same phone that is already hot. A second app would mean a second install, a
 * second storage bucket, a second `navigator.storage.persist()` grant, and a second chance to be
 * running in a Safari tab without noticing — and ENV-1's gate treats a tab as inadmissible.
 *
 * The switcher is two large targets at the top and nothing else. It is not navigation; it is the
 * only choice this app offers.
 *
 * ── Why the fatal sink lives here ────────────────────────────────────────────
 * `setFatalSink` is one process-wide slot. If each screen claimed it on mount and released it on
 * unmount, switching harnesses would leave it unset — precisely when a stray `unhandledrejection`
 * from the screen being torn down is most likely. It is claimed once, here, for the life of the app.
 */

type Unit = 'u0' | 'u1';

export function App() {
  const [unit, setUnit] = useState<Unit>('u0');
  const [fatal, setFatal] = useState<string | null>(null);

  useEffect(() => {
    setFatalSinkBridge(setFatal);
    return () => setFatalSinkBridge(null);
  }, []);

  return (
    <div className="app">
      {/* Above the unit switcher, above everything. A stale instrument produces confidently wrong
          numbers, so this gets the first line on the screen rather than a corner. */}
      <UpdateBanner />

      <div className="unit-switch" role="group" aria-label="Build unit">
        <button
          className={unit === 'u0' ? 'primary' : ''}
          aria-pressed={unit === 'u0'}
          onClick={() => setUnit('u0')}
        >
          U-0 · latency
        </button>
        <button
          className={unit === 'u1' ? 'primary' : ''}
          aria-pressed={unit === 'u1'}
          onClick={() => setUnit('u1')}
        >
          U-1 · audio
        </button>
      </div>

      {fatal && (
        <div className="error">
          <strong>Uncaught error</strong>
          <br />
          {fatal}
        </div>
      )}

      {unit === 'u0' ? <LatencyHarness /> : <AudioHarness />}

      {/* Provenance, always on screen. A result that cannot name the bundle that produced it is
          weaker evidence than it looks. */}
      <p className="build-stamp">build {__BUILD_ID__}</p>
    </div>
  );
}
